import React, { useEffect, useState } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { Spinner } from "react-bootstrap";
import "./YoutubeVideo.css";
import MultiGenreSelect from "../../../components/common/MultiGenreSelect";
import {
  FaYoutube,
  FaSearch,
  FaVideo,
  FaPlay,
  FaPlus,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaSave,
  FaMusic
} from "react-icons/fa";

const YoutubeVideo = () => {
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debounceQuery, setDebounceQuery] = useState(searchQuery);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [workedOnSondId, setWorkedOnSongId] = useState(null);
  const [genres, setGenres] = useState([]);
  const [filterGenre, setFilterGenre] = useState("");
  const [featuredGenresBySong, setFeaturedGenresBySong] = useState({});
  const [savedFeaturedGenresBySong, setSavedFeaturedGenresBySong] = useState({});
  const [savingFeaturedSongId, setSavingFeaturedSongId] = useState(null);
  const [totalVideos, setTotalVideos] = useState(0);

  const fetchGenres = async () => {
    try {
      const response = await allAPiServicesCall.getGenres(
        { page: 1, limit: 1000 },
        getAuthConfig()
      );
      if (response?.success && Array.isArray(response.data)) {
        setGenres(response.data);
      }
    } catch (error) {
      console.error("Error fetching genres:", error);
    }
  };

  useEffect(() => {
    fetchGenres();
  }, []);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const fetchVideos = async (genre = "") => {
    try {
      setLoading(true);
      const response = await allAPiServicesCall.getYoutubeVideos(
        getAuthConfig(),
        { page: currentPage, limit, genre }
      );
      setTotalPages(response.pagination.totalPages);
      setTotalVideos(response.pagination.total || 0);
      setVideos((response.data || []).map(normalizeVideo));
    } catch (error) {
      setError("Failed to fetch videos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFeaturedGenresBySong((prev) => {
      const next = { ...prev };
      videos.forEach((video) => {
        const normalized = normalizeVideo(video);
        if (!normalized?.song_id) return;
        if (!next[normalized.song_id]) {
          next[normalized.song_id] = Array.isArray(normalized.featuredMusicVideoGenres)
            ? normalized.featuredMusicVideoGenres
            : [];
        }
      });
      return next;
    });

    setSavedFeaturedGenresBySong((prev) => {
      const next = { ...prev };
      videos.forEach((video) => {
        const normalized = normalizeVideo(video);
        if (!normalized?.song_id) return;
        if (!next[normalized.song_id]) {
          next[normalized.song_id] = Array.isArray(normalized.featuredMusicVideoGenres)
            ? normalized.featuredMusicVideoGenres
            : [];
        }
      });
      return next;
    });
  }, [videos]);

  const handleFilterGenreChange = (event) => {
    setFilterGenre(event.target.value);
    setCurrentPage(1);
  };

  const isSearching = searchQuery.trim().length > 0;

  const fetchSearchResults = async (query) => {
    try {
      setLoading(true);
      const response = await allAPiServicesCall.getSearchYoutubeVideos(
        { query },
        getAuthConfig()
      );
      if (response.data) {
        setVideos((response.data || []).map(normalizeVideo));
      } else {
        setVideos([]);
        console.log("error fetching videos");
      }
    } catch (error) {
      setError("Failed to fetch videos.");
      console.log("failed to fetch youtube videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSearching) {
      fetchVideos(filterGenre);
    }
  }, [currentPage, isSearching, filterGenre]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (isSearching && debounceQuery.trim()) {
      fetchSearchResults(debounceQuery);
    }
  }, [debounceQuery, isSearching]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFeaturedGenresChange = (songId, genreArray) => {
    setFeaturedGenresBySong((prev) => ({
      ...prev,
      [songId]: genreArray,
    }));
  };

  const normalizeGenreList = (list = []) =>
    list.map((genre) => String(genre).trim()).filter(Boolean).sort();

  const hasFeaturedGenreChanges = (songId) => {
    const current = normalizeGenreList(featuredGenresBySong[songId] || []);
    const saved = normalizeGenreList(savedFeaturedGenresBySong[songId] || []);
    if (current.length !== saved.length) return true;
    return current.some((genre, index) => genre !== saved[index]);
  };

  const handleSaveFeaturedGenres = async (songId) => {
    const genresToSave = featuredGenresBySong[songId] || [];
    try {
      setSavingFeaturedSongId(songId);
      await allAPiServicesCall.updateFeaturedMusicVideoGenres(
        songId,
        genresToSave,
        getAuthConfig()
      );
      setSavedFeaturedGenresBySong((prev) => ({
        ...prev,
        [songId]: genresToSave,
      }));
    } catch (error) {
      console.error("Failed to update featured genres:", error);
    } finally {
      setSavingFeaturedSongId(null);
    }
  };

  async function handleAddToMusicVideoSection(song_id) {
    try {
      setWorkedOnSongId(song_id);
      await allAPiServicesCall.addToMusicVideoSection(
        getAuthConfig(),
        song_id
      );
      setVideos((prev) =>
        prev.filter((video) => normalizeVideo(video).song_id !== song_id)
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setWorkedOnSongId();
    }
  }

  async function handleDeleteFromMusicVideoSection(song_id) {
    try {
      setLoading(true);
      setWorkedOnSongId(song_id);
      await allAPiServicesCall.removeFromMusicVideoSection(
        song_id,
        getAuthConfig()
      );

      setVideos((prev) =>
        prev.filter((video) => normalizeVideo(video).song_id !== song_id)
      );
    } catch (error) {
    } finally {
      setLoading(false);
      setWorkedOnSongId();
    }
  }

  const getYoutubeVideoId = (value) => {
    if (!value || typeof value !== "string") return null;
    const trimmedUrl = value.trim();
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmedUrl)) return trimmedUrl;

    const withProtocol = /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;

    try {
      const parsed = new URL(withProtocol);
      const hostname = parsed.hostname.replace("www.", "");

      if (hostname === "youtu.be") {
        return parsed.pathname.replace("/", "") || null;
      }

      if (hostname === "youtube.com" || hostname.endsWith(".youtube.com")) {
        const vParam = parsed.searchParams.get("v");
        if (vParam) return vParam;

        const pathParts = parsed.pathname.split("/").filter(Boolean);
        const shortsIndex = pathParts.indexOf("shorts");
        if (shortsIndex !== -1 && pathParts[shortsIndex + 1]) {
          return pathParts[shortsIndex + 1];
        }

        const embedIndex = pathParts.indexOf("embed");
        if (embedIndex !== -1 && pathParts[embedIndex + 1]) {
          return pathParts[embedIndex + 1];
        }

        const vIndex = pathParts.indexOf("v");
        if (vIndex !== -1 && pathParts[vIndex + 1]) {
          return pathParts[vIndex + 1];
        }
      }
    } catch (error) {
      const match =
        trimmedUrl.match(/[?&]v=([^&]+)/i) ||
        trimmedUrl.match(/youtu\.be\/([^?&/]+)/i) ||
        trimmedUrl.match(/youtube\.com\/shorts\/([^?&/]+)/i) ||
        trimmedUrl.match(/youtube\.com\/embed\/([^?&/]+)/i) ||
        trimmedUrl.match(/youtube\.com\/v\/([^?&/]+)/i);

      return match ? match[1] : null;
    }
  };

  const getVideoPreview = (video) => {
    const videoId =
      video?.youtubeVideoId ||
      getYoutubeVideoId(video?.youtubeUrl) ||
      getYoutubeVideoId(video?.musicVideoUrl);

    return {
      videoId,
      embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : null,
      thumbnailUrl:
        video?.thumbnailUrl ||
        (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null),
    };
  };

  const normalizeVideo = (video) => {
    if (video && video._doc && typeof video._doc === "object") {
      return video._doc;
    }
    return video;
  };

  return (
    <CommonLayout>
      <div className="youtube-page">
        {/* Header Section */}
        <div className="youtube-header">
          <div className="youtube-header-content">
            <div className="youtube-header-icon">
              <FaYoutube />
            </div>
            <div className="youtube-header-text">
              <h1 className="youtube-title">YouTube Videos</h1>
              <p className="youtube-subtitle">
                Manage music videos for your song catalog
              </p>
            </div>
          </div>
          <div className="youtube-stats">
            <div className="youtube-stat-card">
              <FaVideo className="youtube-stat-icon" />
              <div className="youtube-stat-info">
                <span className="youtube-stat-value">{totalVideos}</span>
                <span className="youtube-stat-label">Total Videos</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter Controls */}
        <div className="youtube-controls">
          <div className="youtube-search-wrapper">
            <FaSearch className="youtube-search-icon" />
            <input
              type="text"
              className="youtube-search-input"
              placeholder="Search for songs to add to music video section..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {loading && <Spinner animation="border" size="sm" className="youtube-search-spinner" />}
          </div>
          <div className="youtube-filter-wrapper">
            <select
              className="youtube-filter-select"
              value={filterGenre}
              onChange={handleFilterGenreChange}
            >
              <option value="">All Genres</option>
              {genres.map((genre) => (
                <option key={genre._id} value={genre.genreName}>
                  {genre.genreName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="youtube-error">
            <span>{error}</span>
          </div>
        )}

        {/* Content */}
        {loading && videos.length === 0 ? (
          <div className="youtube-loading">
            <div className="youtube-loading-spinner">
              <Spinner animation="border" />
            </div>
            <p className="youtube-loading-text">Loading videos...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="youtube-empty">
            <div className="youtube-empty-icon">
              <FaVideo />
            </div>
            <h3 className="youtube-empty-title">No Videos Available</h3>
            <p className="youtube-empty-text">
              {isSearching
                ? "No videos found for your search. Try a different keyword."
                : "Search for songs above to add them to the music video section"}
            </p>
          </div>
        ) : (
          <>
            {/* Search Results Header */}
            {isSearching && (
              <div className="youtube-section-header">
                <div className="youtube-section-icon">
                  <FaSearch />
                </div>
                <h2 className="youtube-section-title">Search Results</h2>
                <span className="youtube-section-count">
                  {videos.length} found
                </span>
              </div>
            )}

            {/* Videos Grid */}
            <div className="youtube-grid">
              {videos.map((rawVideo) => {
                const video = normalizeVideo(rawVideo);
                const { embedUrl, thumbnailUrl } = getVideoPreview(video);

                return (
                  <div className="youtube-card" key={video?._id || video?.song_id}>
                    <div className="youtube-card-media-wrapper">
                      {embedUrl ? (
                        <iframe
                          className="youtube-card-iframe"
                          src={embedUrl}
                          title={video.song_name}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : thumbnailUrl ? (
                        <div className="youtube-card-thumbnail-wrapper">
                          <img
                            src={thumbnailUrl}
                            alt={video.song_name}
                            className="youtube-card-thumbnail"
                          />
                          <div className="youtube-card-overlay">
                            <div className="youtube-card-play">
                              <FaPlay />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="youtube-card-placeholder">
                          <FaVideo />
                          <span>No preview available</span>
                        </div>
                      )}
                      <div className="youtube-card-badge">
                        <FaYoutube /> Video
                      </div>
                    </div>

                    <div className="youtube-card-content">
                      <h3 className="youtube-card-title">{video.song_name}</h3>

                      {/* Genre Badges */}
                      {video.genre && (
                        <div className="youtube-card-genres">
                          {(Array.isArray(video.genre)
                            ? video.genre
                            : String(video.genre).split(",").map((g) => g.trim()).filter(Boolean)
                          ).slice(0, 5).map((genre, index) => (
                            <span key={`${genre}-${index}`} className="youtube-card-genre">
                              {genre}
                            </span>
                          ))}
                          {(Array.isArray(video.genre) ? video.genre : String(video.genre).split(",")).length > 5 && (
                            <span className="youtube-card-genre-more">
                              +{(Array.isArray(video.genre) ? video.genre : String(video.genre).split(",")).length - 5}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Featured Genres (non-search mode) */}
                      {!isSearching && (
                        <div className="youtube-card-featured">
                          <div className="youtube-card-featured-label">
                            <FaMusic /> Featured Genres
                          </div>
                          <div className="youtube-card-featured-row">
                            <div className="youtube-card-featured-select">
                              <MultiGenreSelect
                                genres={genres}
                                selectedGenres={featuredGenresBySong[video.song_id] || []}
                                onChange={(genreArray) =>
                                  handleFeaturedGenresChange(video.song_id, genreArray)
                                }
                                placeholder="Select Featured Genres"
                                size="sm"
                              />
                            </div>
                            <button
                              className="youtube-btn youtube-btn-save"
                              disabled={
                                savingFeaturedSongId === video.song_id ||
                                !hasFeaturedGenreChanges(video.song_id)
                              }
                              onClick={() => handleSaveFeaturedGenres(video.song_id)}
                            >
                              <FaSave />
                              {savingFeaturedSongId === video.song_id ? "Saving" : "Save"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="youtube-card-actions">
                        {isSearching ? (
                          <button
                            className="youtube-btn youtube-btn-add"
                            disabled={video?.song_id === workedOnSondId}
                            onClick={() => handleAddToMusicVideoSection(video?.song_id)}
                          >
                            <FaPlus />
                            {video.song_id === workedOnSondId ? "Adding..." : "Add to Music Videos"}
                          </button>
                        ) : (
                          <button
                            className="youtube-btn youtube-btn-delete"
                            disabled={video?.song_id === workedOnSondId}
                            onClick={() => handleDeleteFromMusicVideoSection(video?.song_id)}
                          >
                            <FaTrash />
                            {video.song_id === workedOnSondId ? "Removing..." : "Remove from Videos"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {!isSearching && totalPages > 1 && (
              <div className="youtube-pagination">
                <button
                  className="youtube-pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </button>
                <div className="youtube-pagination-info">
                  <span className="youtube-pagination-current">{currentPage}</span>
                  <span className="youtube-pagination-separator">/</span>
                  <span className="youtube-pagination-total">{totalPages}</span>
                </div>
                <button
                  className="youtube-pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </CommonLayout>
  );
};

export default YoutubeVideo;
